import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green, purple } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel
} from "@mui/material";
import {
  QuestionAnswer as QuestionIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon
} from "@mui/icons-material";
import RemarketingTaskEditor from "../RemarketingTaskEditor";
import { validateVariableName } from "../../utils/variableValidator";
const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  dialogTitle: {
    padding: theme.spacing(2),
    "& h2": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      "& svg": {
        color: purple[500]
      }
    }
  },
  dialogContent: {
    padding: theme.spacing(2, 3)
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2)
  },
  chip: {
    margin: theme.spacing(0.5),
    cursor: "pointer",
  },
  infoBox: {
    backgroundColor: "rgba(156, 39, 176, 0.05)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    border: `1px solid rgba(156, 39, 176, 0.1)`,
    "& svg": {
      color: purple[500]
    }
  },
  actionButton: {
    borderRadius: 8,
    textTransform: "none",
    padding: theme.spacing(1, 2),
    fontWeight: 500
  },
  remarketingSection: {
    backgroundColor: "rgba(124, 58, 237, 0.05)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid rgba(124, 58, 237, 0.1)`,
  }
}));
const FlowBuilderInputModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [variableName, setVariableName] = useState("");
  const [variableError, setVariableError] = useState(null); // [ANTIGRAVITY] Variable validation error
  const [nodeName, setNodeName] = useState("");
  const [remarketingEnabled, setRemarketingEnabled] = useState(false);
  const [remarketingTasks, setRemarketingTasks] = useState([]);
  const questionInputRef = useRef(null);
  const remarketingEditorRef = useRef(null); // [ANTIGRAVITY] Ref to access pending task
  const exampleVariables = [
    { name: "nombre", description: i18n.t("flowBuilder.conditionCompare.variables.nombre") },
    { name: "email", description: i18n.t("flowBuilder.conditionCompare.variables.email") },
    { name: "telefono", description: i18n.t("flowBuilder.conditionCompare.variables.telefono") },
    { name: "ciudad", description: i18n.t("flowBuilder.conditionCompare.variables.ciudad") }
  ];
  const defaultFinalMessage = "Estuve atento a tu respuesta. Tal vez en estos momentos estés ocupad@, pero, descuida, puedes volver a escribirnos cuando te encuentres disponible para continuar con tu atención. ¡Te esperamos!";
  const [finalTaskEnabled, setFinalTaskEnabled] = useState(true);
  const [finalTaskTime, setFinalTaskTime] = useState(20);
  const [finalTaskUnit, setFinalTaskUnit] = useState("minutes");
  const [finalTaskMessage, setFinalTaskMessage] = useState(defaultFinalMessage);
  useEffect(() => {
    if (open === "edit") {
      setQuestion(data.data.question || "");
      setVariableName(data.data.variableName || "");
      setNodeName(data.data.name || "");
      const enabled = data.data.remarketingEnabled !== undefined ? data.data.remarketingEnabled : true;
      setRemarketingEnabled(enabled);
      // [ANTIGRAVITY] Separar tareas manuales de la Tarea Final
      let tasks = data.data.remarketingTasks || [];
      let foundFinalTask = null;
      if (tasks.length > 0) {
        // Buscamos si la última tarea es la "final"
        const lastTask = tasks[tasks.length - 1];
        if (lastTask.isFinalTask) {
          foundFinalTask = lastTask;
          // Las manuales son todas menos la última
          tasks = tasks.slice(0, tasks.length - 1);
        }
      }
      setRemarketingTasks(tasks);
      // Configurar estado de tarea final
      if (foundFinalTask) {
        setFinalTaskEnabled(true);
        setFinalTaskTime(foundFinalTask.waitTime || 20);
        setFinalTaskUnit(foundFinalTask.waitUnit || "minutes");
        // Extraer mensaje del contenido
        const textContent = foundFinalTask.content?.find(c => c.type === 'text');
        setFinalTaskMessage(textContent?.value || defaultFinalMessage);
      } else {
        setFinalTaskEnabled(false);
        setFinalTaskTime(20);
        setFinalTaskUnit("minutes");
        setFinalTaskMessage(defaultFinalMessage);
      }
      setActiveModal(true);
    } else if (open === "create") {
      setQuestion("");
      setVariableName("");
      setNodeName("");
      // [ANTIGRAVITY] Active automatic remarketing by default for new nodes
      setRemarketingEnabled(true);
      setRemarketingTasks([]);
      setFinalTaskEnabled(true);
      setFinalTaskTime(20);
      setFinalTaskMessage(defaultFinalMessage);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  // Protected Close Handler
  const handleClose = () => {
    // 1. Check for unsaved content in the editor (Partial form)
    if (remarketingEditorRef.current && remarketingEditorRef.current.hasPendingChanges()) {
      if (!window.confirm("Tienes una tarea de remarketing incompleta en el editor. ¿Deseas salir y perder lo que estás escribiendo?")) {
        return;
      }
    }
    // 2. [ANTIGRAVITY] Check for Unsaved Changes in the Task LIST
    // We compare current state vs original data
    const originalFullTasks = (open === "edit" ? data.data?.remarketingTasks : []) || [];
    // Reconstruir estado actual hipotético para comparar
    // Esto es complejo porque la tarea final se separa. 
    // Simplificación: Solo avisar si hay tareas manuales en lista que difieren.
    close(null);
    setActiveModal(false);
    setLoading(false);
  };
  // [ANTIGRAVITY] Handler para validar variable en tiempo real
  const handleVariableNameChange = (e) => {
    const value = e.target.value;
    setVariableName(value);
    const validation = validateVariableName(value);
    setVariableError(validation.error);
  };
  const handleSave = async () => {
    setLoading(true);
    try {
      // [ANTIGRAVITY] Safety Check: Ensure no pending tasks in editor
      if (remarketingEnabled && remarketingEditorRef.current && remarketingEditorRef.current.hasPendingChanges()) {
        if (!window.confirm("Tienes cambios sin guardar en el editor de Tareas de Retargeting. Estos cambios NO se guardarán si continúas. ¿Deseas proceder y descartarlos?")) {
          setLoading(false);
          return;
        }
      }
      // [ANTIGRAVITY] Construir la lista final de tareas
      let allTasks = [...remarketingTasks];
      if (remarketingEnabled && finalTaskEnabled) {
        // Crear objeto de Tarea Final (Cierre)
        const finalTaskObj = {
          id: Date.now() + 999123, // ID ficticio único
          isFinalTask: true, // Para UI
          isLastTask: true,  // Para Backend (cierre de ticket)
          waitTime: Number(finalTaskTime),
          waitUnit: finalTaskUnit || 'minutes',
          variable: `final_closure_${Date.now()}`, // Variable interna autogenerada
          content: [
            { type: 'text', value: finalTaskMessage }
          ],
          order: allTasks.length + 1,
          closeTicket: true // Flag importante para el backend (si lo soporta así)
        };
        allTasks.push(finalTaskObj);
      }
      console.log("[MODAL INPUT] Guardando nodo. Remarketing habilitado:", remarketingEnabled);
      console.log("[MODAL INPUT] Tareas manuales:", remarketingTasks.length);
      console.log("[MODAL INPUT] Total tareas (incluyendo final):", allTasks.length);
      if (open === "edit") {
        const updateData = {
          ...data,
          data: {
            ...data.data,
            question: question,
            variableName: variableName,
            name: nodeName,
            remarketingEnabled: remarketingEnabled,
            remarketingTasks: allTasks
          }
        };
        console.log("[MODAL INPUT] Enviando datos de actualización:", JSON.stringify(updateData.data, null, 2));
        onUpdate(updateData);
        handleClose();
      } else if (open === "create") {
        const createData = {
          question: question,
          variableName: variableName,
          name: nodeName,
          remarketingEnabled: remarketingEnabled,
          remarketingTasks: allTasks
        };
        console.log("[MODAL INPUT] Enviando datos de creación:", JSON.stringify(createData, null, 2));
        onSave(createData);
        handleClose();
      }
      toast.success(i18n.t("flowBuilder.singleBlock.toasts.saveSuccess") || "Contenido guardado con éxito");
    } catch (err) {
      console.error(err);
      toastError(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };
  const handleVariableClick = (variable) => {
    if (questionInputRef.current) {
      const inputElement = questionInputRef.current.querySelector('textarea');
      if (inputElement) {
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        const variableText = `\${${variable.name}}`;
        const newText = question.substring(0, start) + variableText + question.substring(end);
        setQuestion(newText);
        const newCursorPosition = start + variableText.length;
        setTimeout(() => {
          inputElement.focus();
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 100);
      }
    }
  };
  const isFormValid = () => {
    return question.trim() !== "" && variableName.trim() !== "";
  };
  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        TransitionProps={{
          style: {
            transition: "all 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            transform: activeModal ? "none" : "translateY(-20px)",
            opacity: activeModal ? 1 : 0
          }
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" gap={1}>
            <QuestionIcon style={{ color: "#9c27b0" }} />
            <Typography variant="h6">{open === "edit" ? (i18n.t("flowBuilder.input.editTitle") || "Editar input del flujo") : (i18n.t("flowBuilder.input.addTitle") || "Añadir input al flujo")}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent} dividers>
          <Stack spacing={3}>
            <Box>
              <TextField
                label={i18n.t("flowBuilder.input.internalName") || "Nombre Interno"}
                variant="outlined"
                value={nodeName}
                onChange={e => setNodeName(e.target.value)}
                margin="dense"
                fullWidth
                className={classes.textField}
                style={{ width: "100%", marginBottom: "15px" }}
                helperText={i18n.t("flowBuilder.input.internalNameHelper") || "Nombre para identificar este nodo en el editor (ej. 'Bienvenida', 'Menú Principal')"}
              />
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle2">{i18n.t("flowBuilder.input.question") || "Pregunta"}</Typography>
                <Tooltip title={i18n.t("flowBuilder.input.questionHelper") || "La pregunta que será enviada al usuario. Puedes incluir variables usando \${nombreVariable}"}>
                  <IconButton size="small" color="primary">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                label={i18n.t("flowBuilder.input.question") || "Escribe la pregunta"}
                multiline
                rows={4}
                name="question"
                variant="outlined"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                className={classes.textField}
                fullWidth
                ref={questionInputRef}
                placeholder={i18n.t("flowBuilder.input.questionPlaceholder") || "Ex: Hola \${nombre}, ¿cuál es tu edad?"}
              />
              <Box mt={1}>
                <Typography variant="caption" style={{ color: '#aaa', display: 'block', marginBottom: '8px' }}>
                  {i18n.t("flowBuilder.input.availableVariables") || "Variables disponibles (haz clic para insertar):"}
                </Typography>
                <Box display="flex" flexWrap="wrap" mt={0.5}>
                  {exampleVariables.map((variable) => (
                    <Chip
                      key={variable.name}
                      label={variable.name}
                      className={classes.chip}
                      onClick={() => handleVariableClick(variable)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            <Divider />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle2">{i18n.t("flowBuilder.input.variableName") || "Nombre de la variable"}</Typography>
                <Tooltip title={i18n.t("flowBuilder.input.variableHelper") || "Nombre de la variable donde se almacenará la respuesta. No uses espacios ni caracteres especiales."}>
                  <IconButton size="small" color="primary">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                label={i18n.t("flowBuilder.input.variableName") || "Nombre de la variable"}
                name="variableName"
                variant="outlined"
                value={variableName}
                onChange={handleVariableNameChange}
                className={classes.textField}
                fullWidth
                required
                error={Boolean(variableError)}
                placeholder={i18n.t("flowBuilder.input.variablePlaceholder") || "Ex: edad_cliente"}
                helperText={variableError || i18n.t("flowBuilder.input.variableNameHelper") || "Solo minúsculas, números, guiones bajos (_) y guiones (-)"}
              />
            </Box>
            <Divider />
            {/* Remarketing Section */}
            <Box className={classes.remarketingSection}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ScheduleIcon sx={{ color: "#7c3aed" }} />
                  <Typography variant="subtitle2" fontWeight="600">
                    {i18n.t("flowBuilder.remarketing.title") || "Remarketing Automático"}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={remarketingEnabled}
                      onChange={(e) => setRemarketingEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={remarketingEnabled ? (i18n.t("flowBuilder.remarketing.enabled") || "Habilitado") : (i18n.t("flowBuilder.remarketing.disabled") || "Deshabilitado")}
                />
              </Stack>
              {remarketingEnabled && (
                <>
                  <RemarketingTaskEditor
                    ref={remarketingEditorRef}
                    tasks={remarketingTasks}
                    onChange={setRemarketingTasks}
                    maxTasks={30}
                    enableAntiBan={true}
                    allowAuto={false} // [ANTIGRAVITY] Auto disabled as requested
                  />
                  {/* [ANTIGRAVITY] Tarea de Cierre Obligatoria */}
                  <Box style={{
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: 'rgba(211, 47, 47, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 107, 107, 1)',
                    boxShadow: '0 0 25px rgba(255, 107, 107, 0.9), inset 0 0 15px rgba(255, 107, 107, 0.3)',
                    position: 'relative'
                  }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                          {i18n.t("flowBuilder.remarketing.finalTaskTitle") || "🏁 Tarea de Cierre Obligatoria (Final)"}
                        </Typography>
                        <Chip label={i18n.t("flowBuilder.remarketing.automaticTag") || "Automática"} size="small" style={{ backgroundColor: '#ff6b6b', color: '#fff' }} />
                      </Stack>
                      <Switch
                        size="small"
                        checked={finalTaskEnabled}
                        onChange={(e) => setFinalTaskEnabled(e.target.checked)}
                        color="primary"
                      />
                    </Stack>
                    {finalTaskEnabled && (
                      <>
                        <Typography variant="body2" style={{ color: '#ff6b6b', marginBottom: '16px', fontSize: '0.8rem', opacity: 0.9 }}>
                          {i18n.t("flowBuilder.remarketing.finalTaskDesc") || "Esta tarea se ejecutará siempre al final de la secuencia. Cierra el ticket automáticamente."}
                        </Typography>
                    <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2} style={{ marginTop: '8px', marginBottom: '16px' }}>
                        <TextField
                          label={i18n.t("flowBuilder.remarketing.waitTime") || "Espera"}
                          type="number"
                          variant="outlined"
                          size="small"
                          value={finalTaskTime}
                          onChange={(e) => {
                            let val = parseInt(e.target.value);
                            if (val < 1) val = 1;
                                if (val > 360) val = 360;
                            setFinalTaskTime(val);
                          }}
                          InputProps={{ inputProps: { min: 1, max: 360 } }}
                          style={{ width: 120 }}
                        />
                        <TextField
                          select
                          label={i18n.t("flowBuilder.remarketing.waitUnit") || "Unidad"}
                          variant="outlined"
                          size="small"
                          value={finalTaskUnit}
                          onChange={(e) => setFinalTaskUnit(e.target.value)}
                          style={{ width: 120 }}
                        >
                          <MenuItem value="minutes">{i18n.t("flowBuilder.remarketing.units.minutes") || "Minutos"}</MenuItem>
                          <MenuItem value="hours">{i18n.t("flowBuilder.remarketing.units.hours") || "Horas"}</MenuItem>
                          <MenuItem value="days">{i18n.t("flowBuilder.remarketing.units.days") || "Días"}</MenuItem>
                        </TextField>
                        <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.7)', marginLeft: '8px' }}>
                          {i18n.t("flowBuilder.remarketing.afterLastAction") || "después de la última acción."}
                        </Typography>
                      </Stack>
                      <TextField
                        label={i18n.t("flowBuilder.remarketing.finalTaskMessageLabel") || "Mensaje de Cierre"}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        value={finalTaskMessage}
                        onChange={(e) => setFinalTaskMessage(e.target.value)}
                        style={{ marginTop: '16px' }}
                      />
                    </Stack>
                    </>
                  )}
                </Box>
                </>
              )}
            </Box>
            <Box className={classes.infoBox}>
              <Stack direction="row" spacing={1}>
                <InfoIcon fontSize="small" />
                <Box>
                  <Typography variant="subtitle2">{i18n.t("flowBuilder.input.howItWorks") || "Cómo funciona"}</Typography>
                  <Typography variant="body2">
                    {i18n.t("flowBuilder.input.howItWorksDesc") || "Este nodo enviará la pregunta al usuario y esperará una respuesta. Cuando el usuario responda, el valor se almacenará en la variable especificada y el flujo continuará al siguiente nodo."}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            className={classes.secondaryButton}
          >
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <div className={classes.btnWrapper}>
            <Button
              type="submit"
              onClick={handleSave}
              disabled={loading || Boolean(variableError) || !variableName.trim()}
              className={classes.applyButton}
            >
              {open === "edit" ? (i18n.t("common.save") || "Guardar cambios") : (i18n.t("common.add") || "Añadir")}
            </Button>
            {loading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default FlowBuilderInputModal;
